import { useMemo } from 'react';
import {
  Checkbox,
  ComboBox,
  Dropdown,
  RadioButton,
  RadioButtonGroup,
  TextArea,
} from '@carbon/react';
import { useStore } from '@tanstack/react-form';
import moment from 'moment';
import type { ScenariosResponse } from 'types/api-responses';
import { DatePicker } from 'components/date-picker';
import {
  useDcaScenarios,
  useEvaluationCaseDetail,
  useScenarios,
  type WorkingConfigForm,
} from './hooks';
import styles from './eval-settings-form.module.scss';

/** A DCA dropdown option — the fetched scenarios plus a synthetic "Legacy" (`id: null`) default. */
interface DcaOption {
  id: number | null;
  name: string;
}

/** The prepended default — `null` DCA scenario = "Legacy" (mirrors the legacy panel). */
const LEGACY_DCA_OPTION: DcaOption = { id: null, name: 'Legacy' };

/**
 * The Discount To Date modes, one per wire shape (see `WorkingConfig.discountToDateType`).
 *
 * `'case'` is a UI-ONLY sentinel for the form's `''`: Carbon compares a RadioButton's
 * `value` against `valueSelected` by identity, and an empty-string value is too easy to
 * confuse with "nothing selected" — so the blank mode gets a real token here and maps
 * back at the boundary (`toFormMode` / `toRadioMode`).
 *
 * The floating option is labelled "Effective date", NOT the case editor's "Start Of The
 * Month" (`DISCOUNT_TO_DATE_TYPE_OPTIONS`): a floating type resolves the anchor to nil,
 * which the calculator reads as the run's effective date. Deliberate divergence from
 * that pattern — the shared label would name the wrong date here.
 */
const DISCOUNT_MODE_OPTIONS = [
  { value: 'case', label: 'Use case default' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'floating', label: 'Effective date' },
] as const;

/**
 * Maps a radio value back to the form field's wire-shaped mode.
 *
 * @param {string | number | undefined} value - The selected RadioButton value.
 * @return {'' | 'fixed' | 'floating'} The `discountToDateType` to store.
 */
const toFormMode = (
  value: string | number | undefined
): '' | 'fixed' | 'floating' => {
  if (value === 'fixed') {
    return 'fixed';
  }
  return value === 'floating' ? 'floating' : '';
};

/**
 * Combobox display + search text for a Scenario option (module-scoped — no per-render
 * closure). Tolerates the `null` Carbon's `itemToString` contract can pass.
 *
 * @param {ScenariosResponse | null} item - The scenario option, or `null`.
 * @return {string} The scenario name, or an empty string for `null`.
 */
const scenarioItemToString = (item: ScenariosResponse | null) =>
  item?.name ?? '';

export interface EvalSettingsFormProps {
  /** The lifted working-config form handle (form + the config setters). */
  workingConfig: WorkingConfigForm;
  /**
   * True when a run MUST create a new eval, so the `createNewEvalOnCalculate` checkbox is shown
   * checked + disabled (and the payload flag is forced true at the call site):
   *   - there's NO existing eval to update in place — a from-scratch draft OR a fresh workspace
   *     with no eval yet (`calculationId === null`); the BE can't reuse a nonexistent one; OR
   *   - the viewed eval is SAVED — recalculating it must fork, never overwrite the immutable
   *     saved snapshot in place (P8 Save).
   * Otherwise it's false and the checkbox is user-editable (update-in-place vs. fork).
   */
  mustCreateNew?: boolean;
}

/**
 * The Evaluation-settings inputs in the Eval Settings panel, bound to the lifted
 * working-config form: Effective Date, Discount To Date, the DCA Scenario dropdown
 * (options from `useDcaScenarios`, with a "Legacy" = `null` default prepended),
 * Description / Notes, and the `createNewEvalOnCalculate` checkbox (checked +
 * disabled when there's no existing eval to update — `mustCreateNew`). Editing
 * any input marks the form dirty, which enables Calculate in the panel footer.
 *
 * `effectiveDate` / `discountToDate` / `dcaScenarioId` route through the dedicated
 * form setters (`setEffectiveDate` / `setDiscountToDate` / `setDcaScenarioId`);
 * `description` / `notes` / the checkbox
 * bind directly via `form.setFieldValue` (they have no cascade, unlike the slot
 * overrides). Values are read off the form store so the inputs stay controlled.
 *
 * @param {EvalSettingsFormProps} props - The working-config form handle + the draft flag.
 * @return {JSX.Element} The Evaluation-settings form fields.
 */
export function EvalSettingsForm({
  workingConfig,
  mustCreateNew = false,
}: EvalSettingsFormProps) {
  const {
    form,
    setEffectiveDate,
    setDiscountToDate,
    setDiscountToDateType,
    setScenarioId,
    setDcaScenarioId,
  } = workingConfig;
  const { data: dcaScenarios } = useDcaScenarios();
  const { data: scenarios } = useScenarios();

  // Subscribe per-field (not to the whole `state.values`) so this form re-renders only
  // when ITS inputs change — not on every slot-override edit made from `SlotView`, which
  // writes to the same shared form store. Keeps the render scoping the panel extraction
  // was built for.
  const effectiveDate = useStore(
    form.store,
    (state) => state.values.effectiveDate
  );
  const discountToDate = useStore(
    form.store,
    (state) => state.values.discountToDate
  );
  const discountToDateType = useStore(
    form.store,
    (state) => state.values.discountToDateType
  );
  const scenarioId = useStore(form.store, (state) => state.values.scenarioId);
  const dcaScenarioId = useStore(
    form.store,
    (state) => state.values.dcaScenarioId
  );
  const evaluationCaseId = useStore(
    form.store,
    (state) => state.values.evaluationCaseId
  );
  const description = useStore(form.store, (state) => state.values.description);
  const notes = useStore(form.store, (state) => state.values.notes);
  const createNewEvalOnCalculate = useStore(
    form.store,
    (state) => state.values.createNewEvalOnCalculate
  );

  // The active case — read for its discount-to anchor (below). Same query key as the
  // panel's and CaseManagement's reads, so React Query dedupes: no extra request.
  const { data: activeCaseDetail } = useEvaluationCaseDetail(evaluationCaseId);

  // The MIDDLE rung of the server's discount-to ladder (override -> case -> effective
  // date, `EvaluationCalculation#resolved_discount_to_date`), mirrored here so the blank
  // field shows what the run will ACTUALLY anchor on. A 'floating' case is deliberately
  // null: it ignores its own stored date and tracks the effective date, so it falls
  // through to the last rung. Without this, a fixed case's date was invisible pre-run —
  // Eval Settings showed the effective date while the post-run header (which reads the
  // server-resolved value) showed the case's, and the two silently disagreed.
  const caseDiscountAnchor =
    activeCaseDetail?.discountToDateType === 'fixed'
      ? activeCaseDetail.discountToDate
      : null;

  // "Legacy" (null) first, then the fetched DCA scenarios. Resolved selection falls back
  // to Legacy when the form holds null (or an id not yet in the loaded list).
  const dcaOptions = useMemo<DcaOption[]>(
    () => [
      LEGACY_DCA_OPTION,
      ...(dcaScenarios ?? []).map((scenario) => ({
        id: scenario.id,
        name: scenario.name,
      })),
    ],
    [dcaScenarios]
  );
  const selectedDca =
    dcaOptions.find((option) => option.id === dcaScenarioId) ??
    LEGACY_DCA_OPTION;
  // A case's run takes its DCA (and Type Curve) from the case, so it offers no DCA pick. A run with
  // no case keeps it (D7).
  const caseLevel = evaluationCaseId !== null;

  // The org-wide Scenario is a REQUIRED pick (no "Legacy"/null option, unlike DCA) — so
  // the resolved selection is `null` until the user chooses one (or the seeded id lands
  // in the loaded list), and Calculate stays gated off meanwhile.
  const selectedScenario =
    (scenarios ?? []).find((scenario) => scenario.id === scenarioId) ?? null;

  return (
    <div className={styles.form}>
      {/* Select Scenario — the primary calc input (the org-wide composable case/price-deck
          bundle). Required to Calculate, so it leads the form. A searchable ComboBox
          (scenario lists can be long), mirroring the legacy panel's "Select Scenario". */}
      <div className={styles.row}>
        <div className={styles.field}>
          <ComboBox<ScenariosResponse>
            id="eval3-scenario"
            titleText="Select Scenario"
            placeholder="Type to search scenarios"
            items={scenarios ?? []}
            itemToString={scenarioItemToString}
            selectedItem={selectedScenario}
            shouldFilterItem={({ item, inputValue }) => {
              if (!inputValue) {
                return true;
              }
              return scenarioItemToString(item)
                .toLowerCase()
                .includes(inputValue.toLowerCase());
            }}
            onChange={({
              selectedItem,
            }: {
              selectedItem?: ScenariosResponse | null;
            }) => {
              // Guard against a no-op change: Carbon fires onChange when the controlled
              // `selectedItem` resolves (e.g. the async `/scenarios` load lands on the
              // seeded id), which would otherwise call the setter and mark a pristine
              // seeded form dirty (wrongly enabling Calculate). Only write on a real change.
              const next = selectedItem?.id ?? null;
              if (next !== scenarioId) {
                setScenarioId(next);
              }
            }}
            size="sm"
          />
        </div>
      </div>

      {/* Effective Date + Discount To Date + DCA as a three-up row — mirrors the mock's
          compact paired rows (the mock pairs Effective Date with Time Interval, which
          lives in the sibling OutputDisplayControls; here it carries the discount-to
          date and the DCA dropdown instead). `.field` flexes, so the row divides
          evenly however many fields it holds. */}
      <div className={styles.row}>
        <div className={styles.field}>
          <DatePicker
            id="eval3-effective-date"
            labelText="Effective Date"
            value={effectiveDate || undefined}
            size="sm"
            onChange={(dates: Date[]) => {
              // Carbon fires onChange with an EMPTY `selectedDates` when the value is
              // applied programmatically (the seed) — ignore it, or it clobbers the
              // just-seeded date back to ''. The effective date is required (it gates
              // Calculate), so there's no "clear to empty" flow to support here.
              const [date] = dates;
              if (date) {
                setEffectiveDate(moment(date).format('YYYY-MM-DD'));
              }
            }}
          />
        </div>
        {!caseLevel && (
          <div className={styles.field}>
            <Dropdown<DcaOption>
              id="eval3-dca-scenario"
              titleText="DCA Scenario"
              label="Select a DCA scenario"
              items={dcaOptions}
              itemToString={(item) => item?.name ?? ''}
              selectedItem={selectedDca}
              onChange={({ selectedItem }) =>
                setDcaScenarioId(selectedItem?.id ?? null)
              }
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Discount To Date gets its own row: the three-way mode radio needs the full
          width, and pairing it with the date input keeps the mode and the value it
          governs adjacent. */}
      <div className={styles.row}>
        <div className={styles.field}>
          <RadioButtonGroup
            legendText="Discount To Date"
            name="eval3-discount-to-date-type"
            orientation="horizontal"
            valueSelected={discountToDateType || 'case'}
            onChange={(value) => setDiscountToDateType(toFormMode(value))}
          >
            {DISCOUNT_MODE_OPTIONS.map((option) => (
              <RadioButton
                key={option.value}
                id={`eval3-discount-to-date-${option.value}`}
                value={option.value}
                labelText={option.label}
              />
            ))}
          </RadioButtonGroup>
          <DatePicker
            id="eval3-discount-to-date"
            labelText="Date"
            // Outside 'fixed' the input is a PREVIEW of the anchor the run will resolve
            // to — the same override -> case -> effective-date ladder the server walks —
            // so this field and the post-run overview header agree. `readOnly` (a real
            // Carbon prop: it sets input.readOnly and `clickOpens: false`) rather than
            // `disabled`, because the shared wrapper turns `disabled` into its
            // "marked for destruction" line-through class.
            readOnly={discountToDateType !== 'fixed'}
            // One preview per mode, each the anchor the BE would resolve for it:
            //   'fixed'    the user's own date;
            //   'floating' the effective date — this mode's whole point is escaping the
            //              case's anchor, so previewing that anchor would contradict it;
            //   ''         the case's anchor if it has a live one, else the effective date.
            value={
              discountToDateType === 'fixed'
                ? discountToDate || undefined
                : discountToDateType === 'floating'
                  ? effectiveDate || undefined
                  : caseDiscountAnchor || effectiveDate || undefined
            }
            // Discounting to a date BEFORE the effective date floors every time delta
            // at zero, collapsing the valuation toward a plain nominal sum — silently,
            // with no error. Floor the calendar at the effective date so the invalid
            // range can't be picked; the panel's Calculate gate carries the matching
            // rung for a value that arrives any other way (a seeded run, a typed date).
            minDate={
              effectiveDate
                ? moment(effectiveDate, 'YYYY-MM-DD').toDate()
                : undefined
            }
            size="sm"
            onChange={(dates: Date[]) => {
              // Same Carbon quirk as Effective Date: an empty `selectedDates` fires on
              // the programmatic seed, so onChange stays set-only — clearing here would
              // clobber the just-seeded date back to ''.
              const [date] = dates;
              if (date) {
                setDiscountToDate(moment(date).format('YYYY-MM-DD'));
              }
            }}
            // Emptying the text is a legitimate "let me retype that" inside 'fixed'
            // mode — no longer the route OUT of an override (the radio owns that now).
            // onInputChange fires only on real keystrokes (flatpickr never emits it
            // programmatically), which is what distinguishes a user emptying the field
            // from the seed's empty onChange. 'fixed' with no date is a half-filled
            // state, so the panel's Calculate gate carries a rung for it rather than
            // letting the builder quietly downgrade it to "no override".
            onInputChange={(event) => {
              if (event.target.value === '') {
                setDiscountToDate('');
              }
            }}
          />
        </div>
      </div>

      {/* Create-new sits ABOVE the Description/Notes grid (mock order). With no eval
          to update in place (draft or a fresh workspace), a run must create one, so
          the toggle is shown checked + disabled — only once a persisted eval exists
          can the user choose update-in-place vs. fork. */}
      <Checkbox
        id="eval3-create-new"
        labelText="Check this box to create a new eval when you click Calculate"
        checked={mustCreateNew || createNewEvalOnCalculate}
        disabled={mustCreateNew}
        onChange={(_event, { checked }: { checked: boolean }) =>
          form.setFieldValue('createNewEvalOnCalculate', checked)
        }
      />

      {/* Description + Notes side by side — the mock's `evalTextareaGrid` two-column grid. */}
      <div className={styles.textareaGrid}>
        <TextArea
          id="eval3-description"
          labelText="Description"
          rows={3}
          value={description ?? ''}
          onChange={(event) =>
            form.setFieldValue('description', event.target.value)
          }
        />
        <TextArea
          id="eval3-notes"
          labelText="Notes"
          rows={3}
          value={notes ?? ''}
          onChange={(event) => form.setFieldValue('notes', event.target.value)}
        />
      </div>
    </div>
  );
}
